import { Errno, WasiError } from "./errno.ts";

/**
 * Shared memory allocator with synchronization primitives.
 *
 * Implements a buddy allocator and mutual exclusion lock inside a SharedArrayBuffer.
 * Enables multiple worker processes to allocate, share, and access filesystem data
 * structures in shared memory.
 */

// Header words. The buffer is a plain Int32Array as far as this file cares.
const LOCK = 0;
const BUMP = 1; // first byte never yet handed out
const ROOT = 2; // offset of the root directory's inode, for whoever owns the tree
const NEXT_INO = 3;
const ID = 4; // four words of it
const FREE = 8; // one free-list head per size class, from here
export const HEADER_BYTES = 256;

/**
 * A buddy allocator. Sizes are rounded up to a power of two, and a block of
 * 2^k bytes starts at a multiple of 2^k. A freed block merges with its buddy,
 * the other half of the 2^(k+1) block, when that half is free. An allocation
 * splits a larger free block when its own class has none.
 */
const MIN_SHIFT = 5; // 32 bytes, the smallest block
const CLASSES = 26; // up to 2^30, one gigabyte
const MAX_SHIFT = MIN_SHIFT + CLASSES - 1;

/** The first header word past the free lists: the words from here to HEADER_BYTES are the owner's. */
export const OWNER_WORDS = FREE + CLASSES;

// Block header words: the class, then whether the block is on a free list.
// A free block keeps its list links in the first two words after the header.
const BLOCK_HEADER = 8;
const B_SHIFT = 0;
const B_FREE = 1;
const B_NEXT = 2;
const B_PREV = 3;

// Lock word values.
const UNLOCKED = 0;
const LOCKED = 1;
const LOCKED_WITH_SLEEPERS = 2;

/** Number of spin iterations before sleeping on the lock. */
const SPINS = 2000;

/**
 * Checks whether Atomics.wait is supported on the current thread.
 * Returns false on browser main threads where blocking is prohibited.
 */
const CAN_WAIT = (() => {
    try {
        Atomics.wait(new Int32Array(new SharedArrayBuffer(4)), 0, 1, 0);
        return true;
    } catch {
        return false;
    }
})();

export class Arena {
    readonly sab: SharedArrayBuffer;
    words!: Int32Array;
    bytes!: Uint8Array;
    doubles!: Float64Array;

    private constructor(sab: SharedArrayBuffer) {
        this.sab = sab;
        this.#refresh();
    }

    /** Recreates typed array views over the buffer after resize. */
    #refresh(): void {
        const length = this.sab.byteLength;
        if (this.bytes?.length === length) return;
        this.words = new Int32Array(this.sab, 0, length >>> 2);
        this.bytes = new Uint8Array(this.sab, 0, length);
        this.doubles = new Float64Array(this.sab, 0, length >>> 3);
    }

    /** A new, empty arena. Nothing is allocated in it and the root is zero. */
    static create(initialBytes: number, maxBytes: number): Arena {
        const sab = new SharedArrayBuffer(align(initialBytes), { maxByteLength: align(maxBytes) });
        const arena = new Arena(sab);
        Atomics.store(arena.words, BUMP, HEADER_BYTES);
        Atomics.store(arena.words, NEXT_INO, 1);
        const id = crypto.getRandomValues(new Uint32Array(4));
        for (let i = 0; i < 4; i++) Atomics.store(arena.words, ID + i, id[i]! | 0);
        return arena;
    }

    /** Attaches to an existing SharedArrayBuffer. */
    static attach(sab: SharedArrayBuffer): Arena {
        return new Arena(sab);
    }

    /** Unique arena ID, stable across attach calls. */
    get id(): string {
        let out = "";
        for (let i = 0; i < 4; i++) {
            out += (Atomics.load(this.words, ID + i) >>> 0).toString(16).padStart(8, "0");
        }
        return out;
    }

    /** High-water mark of allocated memory in bytes. */
    get used(): number {
        return this.#bumpEnd();
    }

    get root(): number {
        return Atomics.load(this.words, ROOT);
    }

    set root(offset: number) {
        Atomics.store(this.words, ROOT, offset);
    }

    nextIno(): number {
        return Atomics.add(this.words, NEXT_INO, 1);
    }

    /** Executes a callback while holding the arena mutual exclusion lock. */
    locked<T>(f: () => T): T {
        this.#lock();
        this.#refresh();
        try {
            return f();
        } finally {
            this.#unlock();
        }
    }

    #lock(): void {
        while (!this.#spinForLock()) {
            if (CAN_WAIT) {
                this.#sleepForLock();
                return;
            }
        }
    }

    #spinForLock(): boolean {
        for (let spins = 0; spins < SPINS; spins++) {
            if (
                Atomics.load(this.words, LOCK) === UNLOCKED &&
                Atomics.compareExchange(this.words, LOCK, UNLOCKED, LOCKED) === UNLOCKED
            ) {
                return true;
            }
        }
        return false;
    }

    /** Blocks the current thread until the lock is acquired. */
    #sleepForLock(): void {
        while (Atomics.exchange(this.words, LOCK, LOCKED_WITH_SLEEPERS) !== UNLOCKED) {
            Atomics.wait(this.words, LOCK, LOCKED_WITH_SLEEPERS, 50);
        }
    }

    #unlock(): void {
        if (Atomics.exchange(this.words, LOCK, UNLOCKED) === LOCKED_WITH_SLEEPERS) {
            Atomics.notify(this.words, LOCK, 1);
        }
    }

    /** Allocates a zeroed block of at least size bytes. Call under lock. */
    alloc(size: number): number {
        const shift = classOf(size + BLOCK_HEADER);
        const block = this.#takeFree(shift) || this.#bump(shift);
        this.#setBlock(block, B_SHIFT, shift);
        this.#setBlock(block, B_FREE, 0);
        this.bytes.fill(0, block + BLOCK_HEADER, block + (1 << shift));
        return block + BLOCK_HEADER;
    }

    /** Frees a previously allocated block. Call under lock. */
    free(ptr: number): void {
        if (ptr === 0) return;
        const block = ptr - BLOCK_HEADER;
        this.#release(block, this.#getBlock(block, B_SHIFT));
    }

    /** A free block of class `shift`, split from a larger one if need be, or 0. */
    #takeFree(shift: number): number {
        for (let from = shift; from <= MAX_SHIFT; from++) {
            const block = Atomics.load(this.words, FREE + from - MIN_SHIFT);
            if (block === 0) continue;
            this.#unlink(block, from);
            while (from > shift) {
                from--;
                this.#push(block + (1 << from), from);
            }
            return block;
        }
        return 0;
    }

    /** Allocates a new block by advancing the bump pointer. */
    #bump(shift: number): number {
        for (;;) {
            const block = this.#bumpEnd();
            const grow = Math.min(shift, alignmentOf(block));
            const end = block + (1 << grow);
            this.#reserve(end);
            Atomics.store(this.words, BUMP, end);
            if (grow === shift) return block;
            this.#release(block, grow);
            const merged = this.#takeFree(shift);
            if (merged !== 0) return merged;
        }
    }

    #release(block: number, shift: number): void {
        while (shift < MAX_SHIFT) {
            const buddy = block ^ (1 << shift);
            if (
                buddy < HEADER_BYTES ||
                buddy + (1 << shift) > this.#bumpEnd() ||
                this.#getBlock(buddy, B_FREE) === 0 ||
                this.#getBlock(buddy, B_SHIFT) !== shift
            ) {
                break;
            }
            this.#unlink(buddy, shift);
            block = Math.min(block, buddy);
            shift++;
        }
        this.#push(block, shift);
    }

    #push(block: number, shift: number): void {
        const head = FREE + shift - MIN_SHIFT;
        const next = Atomics.load(this.words, head);
        this.#setBlock(block, B_SHIFT, shift);
        this.#setBlock(block, B_FREE, 1);
        this.#setBlock(block, B_NEXT, next);
        this.#setBlock(block, B_PREV, 0);
        if (next !== 0) this.#setBlock(next, B_PREV, block);
        Atomics.store(this.words, head, block);
    }

    #unlink(block: number, shift: number): void {
        const next = this.#getBlock(block, B_NEXT);
        const prev = this.#getBlock(block, B_PREV);
        if (prev === 0) Atomics.store(this.words, FREE + shift - MIN_SHIFT, next);
        else this.#setBlock(prev, B_NEXT, next);
        if (next !== 0) this.#setBlock(next, B_PREV, prev);
        this.#setBlock(block, B_FREE, 0);
    }

    /** Unsigned, because a full two-gigabyte arena ends at 2^31. */
    #bumpEnd(): number {
        return Atomics.load(this.words, BUMP) >>> 0;
    }

    #getBlock(block: number, word: number): number {
        return Atomics.load(this.words, (block >> 2) + word);
    }

    #setBlock(block: number, word: number, value: number): void {
        Atomics.store(this.words, (block >> 2) + word, value);
    }

    /** Grow the buffer until `end` is inside it. */
    #reserve(end: number): void {
        if (end <= this.sab.byteLength) return;
        const max = this.sab.maxByteLength ?? this.sab.byteLength;
        if (end > max) throw new WasiError(Errno.NOSPC);
        // Double buffer capacity to amortize growth cost.
        this.sab.grow(Math.min(max, Math.max(end, this.sab.byteLength * 2)));
        this.#refresh();
    }
}

/** The bytes an allocation of `size` can hold: its whole block less the header. */
export function blockCapacity(size: number): number {
    return (1 << classOf(size + BLOCK_HEADER)) - BLOCK_HEADER;
}

/** The smallest power of two that holds `size` bytes, as a shift. */
function classOf(size: number): number {
    let shift = MIN_SHIFT;
    while (1 << shift < size) shift++;
    if (shift >= MIN_SHIFT + CLASSES) throw new WasiError(Errno.NOSPC);
    return shift;
}

/** The largest class whose blocks may start at `offset`. */
function alignmentOf(offset: number): number {
    return offset === 0 ? MAX_SHIFT : 31 - Math.clz32(offset & -offset);
}

function align(bytes: number): number {
    return Math.max(HEADER_BYTES, Math.ceil(bytes / 8) * 8);
}
